import { type BaseSyntheticEvent, useCallback } from 'react';
import { Form as BSForm, type FormProps as BSFormProps } from 'react-bootstrap';
import {
  type FieldValues,
  FormProvider as RHFProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  FormProvider as DSFormProvider,
  type ResolveFormError,
} from '@hostfully/ui-components';
import type {
  FormContextParts,
  FormDirtyCheckerOptions,
} from '@/components/forms/form/Form.types.ts';

interface Props<FormValues extends FieldValues> {
  defaultValues?: { [key: string]: unknown };
  onSubmit: (
    data: FormValues,
    event: BaseSyntheticEvent | undefined,
    formContextParts: FormContextParts<FormValues>,
  ) => void;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onInvalid?: (_: any) => void;
  schema?: { [key: string]: any };
  resolver?: any;
  children: any;
  disabled?: boolean;
  [key: string]: any;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

type FormProps = Omit<BSFormProps, 'onSubmit'>;

type FormWithProviderProps<FormValues extends FieldValues> = Props<FormValues> &
  FormProps & {
    dirtyChecker?: boolean;
    dirtyCheckerOptions?: FormDirtyCheckerOptions;
  };

export const FormProvider = <FormValues extends FieldValues>({
  defaultValues,
  disabled,
  children,
  schema,
  resolver,
}: Omit<Props<FormValues>, 'onSubmit' | 'onInvalid'>) => {
  const methods = useForm({
    defaultValues,
    disabled,
    resolver: resolver
      ? yupResolver(resolver)
      : yupResolver(Yup.object(schema).required()),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
  });

  const { t } = useTranslation();
  const resolveError = useCallback<ResolveFormError>(
    (key, values) => t(key, values ?? {}) as string,
    [t],
  );

  return (
    <RHFProvider {...methods}>
      <DSFormProvider resolveError={resolveError}>{children}</DSFormProvider>
    </RHFProvider>
  );
};

export const Form = <FormValues extends FieldValues>({
  children,
  onSubmit,
  onInvalid,
  ...props
}: Pick<Props<FormValues>, 'children' | 'onSubmit' | 'onInvalid'> &
  FormProps) => {
  const {
    // form state needs to be deconstructed, otherwise its performance optimisations may result in not having the latest changes propagated in e.g. dirtyFields (ref: https://www.react-hook-form.com/api/useformstate/)
    formState: { defaultValues, dirtyFields, errors },
    handleSubmit,
    reset,
    setError,
    clearErrors,
  } = useFormContext<FormValues>();

  const submitHandler = (
    data: FormValues,
    event: BaseSyntheticEvent | undefined,
  ) => {
    const formState = { defaultValues, dirtyFields, errors };
    return onSubmit(data, event, { formState, reset, setError, clearErrors }); // must return in order to be able to await for async submits
  };

  return (
    <BSForm onSubmit={handleSubmit(submitHandler, onInvalid)} {...props}>
      {children}
    </BSForm>
  );
};

/**
 * This component is the default Form that should be used in the app.
 * It wraps the react-hook-form FormProvider and adds a validation schema as a prop.
 * Any new modifications in the UI and validation should be done here.
 */
const FormWithProvider = <FormValues extends FieldValues>({
  defaultValues,
  disabled,
  children,
  onSubmit,
  onInvalid,
  schema,
  resolver,
  ...rest
}: FormWithProviderProps<FormValues>) => {
  return (
    <FormProvider
      defaultValues={defaultValues}
      disabled={disabled}
      schema={schema}
      resolver={resolver}
    >
      <Form onSubmit={onSubmit} onInvalid={onInvalid} {...rest}>
        {/* TODO add dirty checker when required */}
        {/*{dirtyChecker && <FormDirtyChecker options={dirtyCheckerOptions} />}*/}
        {children}
      </Form>
    </FormProvider>
  );
};

export default FormWithProvider;
